// MarkPaidModal — captures one payment for a subscription_ledger row.
// For method=cash, requests browser geolocation as an anti-fraud signal.
// For UPI/NEFT/cheque, geolocation is skipped (provider is the source of truth).

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { useOffline, type SubmitResult } from '../offline/context.js';
import { friendly_error } from '../lib/friendly-error.js';

type Method = 'upi' | 'cash' | 'cheque' | 'neft' | 'bank_transfer';

interface Props {
  subscription_id: string;
  expected_paise: number;
  total_paid_paise: number;
  subscriber_name: string;
  /** Optional — when supplied, an online-successful mark_paid fires an auto
   *  receipt notification via orpc.notify.send. The dispatcher respects the
   *  tenant's auto_send_receipt setting under the hood. */
  subscriber_id?: string;
  on_close: () => void;
}

type ScreenshotCt = 'image/jpeg' | 'image/png' | 'image/webp';
function screenshot_ct(file_type: string): ScreenshotCt {
  return file_type === 'image/png' ? 'image/png' : file_type === 'image/webp' ? 'image/webp' : 'image/jpeg';
}

const METHOD_KEYS: Method[] = ['upi', 'cash', 'cheque', 'neft', 'bank_transfer'];

export function MarkPaidModal(props: Props): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const lang = current_language();

  const remaining = Math.max(0, props.expected_paise - props.total_paid_paise);
  const [amount_rupees, set_amount_rupees] = useState(Math.round(remaining / 100));
  const [method, set_method] = useState<Method>('upi');
  const [reference, set_reference] = useState('');
  const [note, set_note] = useState('');
  const [geo, set_geo] = useState<{ lat: number; lon: number } | null>(null);
  const [geo_status, set_geo_status] = useState<'idle' | 'requesting' | 'ok' | 'denied'>('idle');

  const capture_geo = (): void => {
    if (!navigator.geolocation) {
      set_geo_status('denied');
      return;
    }
    set_geo_status('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set_geo({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        set_geo_status('ok');
      },
      () => set_geo_status('denied'),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8_000 },
    );
  };

  // Optional payment screenshot (presigned PUT → store the key).
  const [screenshot_key, set_screenshot_key] = useState<string | null>(null);
  const [shot_status, set_shot_status] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const upload_screenshot = async (file: File): Promise<void> => {
    const ct = screenshot_ct(file.type);
    set_shot_status('uploading');
    try {
      const { upload_url, key } = await orpc.ledger.presign_payment_screenshot({ content_type: ct });
      // DRY_RUN dev: storage returns a dryrun.local URL we can't PUT to — just attach the key.
      if (!upload_url.includes('dryrun.local')) {
        const resp = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': ct }, body: file });
        if (!resp.ok) throw new Error(`upload ${resp.status}`);
      }
      set_screenshot_key(key);
      set_shot_status('done');
    } catch {
      set_shot_status('error');
    }
  };

  // Only the queued (offline) state is held locally now — online success
  // closes the modal directly instead of swapping the modal into a "receipt
  // preview" mode.
  const [queued_id, set_queued_id] = useState<string | null>(null);
  const { submit_mark_paid } = useOffline();

  const m = useMutation({
    mutationFn: async (): Promise<SubmitResult> =>
      submit_mark_paid({
        subscription_id: props.subscription_id,
        payment: {
          amount_paise: amount_rupees * 100,
          method,
          ...(reference ? { reference } : {}),
          ...(note ? { note } : {}),
          ...(geo ? { geo } : {}),
          ...(screenshot_key ? { screenshot_key } : {}),
        },
      }),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['dues'] });
      await qc.invalidateQueries({ queryKey: ['ledger-matrix'] });
      if (result.status === 'queued') {
        // Offline path: surface the queued banner so the operator knows the
        // payment is durable but unsent. They dismiss with the Close button.
        set_queued_id(result.queued_id);
        return;
      }
      // Online success: invalidations are awaited above so the parent (dues
      // list / ledger matrix) re-renders with the new payment before we
      // unmount. Then close.
      //
      // The receipt-PDF prefetch that used to live here gated modal close on
      // the PDF render finishing — and silently swallowed failures, leaving
      // the modal open with no visible feedback when render failed (Atlas M0
      // queue lag, missing template, etc.). It's fire-and-forget now: the
      // dues row can offer a "Download receipt" link separately if needed.
      void orpc.pdf
        .receipt({ subscription_id: props.subscription_id })
        .catch(() => undefined);
      // Auto-receipt — fire-and-forget. The dispatcher checks the tenant's
      // notification_prefs.auto_send_receipt toggle when trigger='auto' and
      // skips the whole send when it's false. Errors are swallowed so a
      // notification glitch never blocks the modal close.
      if (props.subscriber_id) {
        void orpc.notify
          .send({
            subscriber_id: props.subscriber_id,
            channels: ['whatsapp', 'email'],
            intent: 'payment_receipt',
            context: { subscription_id: props.subscription_id },
            trigger: 'auto',
            idempotency_key: `auto_receipt.${props.subscription_id}.${Date.now()}`,
          })
          .catch(() => undefined);
      }
      props.on_close();
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    m.mutate();
  };

  const label_cls = `block mb-1 text-sm ${lang === 'ta' ? 'font-tamil' : 'font-sans'}`;
  const input_cls =
    'w-full px-3 py-2 border border-line rounded bg-bg-elev focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm">
      <div className="bg-surface border border-line rounded-lg w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-tamil text-primary mb-1">{t('mark_paid.title')}</h2>
        <p className="text-sm text-ink/60 mb-4">
          <span className={lang === 'ta' ? 'font-tamil' : ''}>{props.subscriber_name}</span>
          {' · '}
          <span className="font-mono text-xs">
            {format_inr(props.total_paid_paise)} / {format_inr(props.expected_paise)}
          </span>
        </p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label>
            <span className={label_cls}>{t('mark_paid.amount')}</span>
            <div className="flex items-center gap-2">
              <span className="text-ink/60 text-sm">₹</span>
              <input
                type="number"
                min={1}
                className={`${input_cls} font-mono`}
                value={amount_rupees}
                onChange={(e) => set_amount_rupees(Number(e.target.value))}
                required
              />
            </div>
          </label>
          <div>
            <span className={label_cls}>{t('mark_paid.method')}</span>
            <div className="grid grid-cols-2 gap-2">
              {METHOD_KEYS.map((opt) => (
                <button
                  type="button"
                  key={opt}
                  onClick={() => set_method(opt)}
                  className={`px-3 py-2 rounded text-xs font-medium border ${
                    method === opt
                      ? 'bg-primary-soft border-primary text-primary-ink'
                      : 'bg-bg-elev border-line text-ink/60'
                  }`}
                >
                  {t(`mark_paid.method_${opt}` as const)}
                </button>
              ))}
            </div>
          </div>
          <label>
            <span className={label_cls}>{t('mark_paid.reference')}</span>
            <input
              type="text"
              className={`${input_cls} font-mono text-sm`}
              value={reference}
              onChange={(e) => set_reference(e.target.value)}
              placeholder={method === 'upi' ? 'pay_XXX' : ''}
            />
          </label>
          <label>
            <span className={label_cls}>{t('mark_paid.note')}</span>
            <textarea
              className={`${input_cls} text-sm`}
              rows={2}
              value={note}
              onChange={(e) => set_note(e.target.value)}
            />
          </label>
          <div>
              <span className={label_cls}>
                {lang === 'ta' ? 'திரைப்பிடிப்பு (விருப்பம்)' : 'Screenshot (optional)'}
              </span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="text-xs"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void upload_screenshot(file);
                }}
              />
              {shot_status === 'uploading' && <span className="ml-2 text-xs text-ink/60">…</span>}
              {shot_status === 'done' && <span className="ml-2 text-xs text-good">✓ {lang === 'ta' ? 'பதிவேற்றப்பட்டது' : 'attached'}</span>}
              {shot_status === 'error' && (
                <span className="ml-2 text-xs text-warn">
                  ⚠ {lang === 'ta' ? 'பதிவேற்ற முடியவில்லை (இணைய இணைப்பு தேவை)' : 'upload failed (needs connectivity)'}
                </span>
              )}
          </div>
          {method === 'cash' && (
            <div className="text-xs">
              {geo_status === 'idle' && (
                <button
                  type="button"
                  onClick={capture_geo}
                  className="text-primary underline"
                >
                  {t('mark_paid.gps_capture')}
                </button>
              )}
              {geo_status === 'requesting' && <span className="text-ink/60">…</span>}
              {geo_status === 'ok' && geo && (
                <span className="text-good">
                  ✓ {t('mark_paid.gps_captured')} ({geo.lat.toFixed(4)}, {geo.lon.toFixed(4)})
                </span>
              )}
              {geo_status === 'denied' && (
                <span className="text-warn">⚠ GPS not available</span>
              )}
            </div>
          )}
          {m.error && (
            <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded">
              {friendly_error(m.error)}
            </div>
          )}
          {queued_id && (
            <div className="px-3 py-2 bg-warn-soft text-warn text-xs rounded">
              📡 {t('mark_paid.queued_offline', 'Saved offline. Will sync when online.')}
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={props.on_close}
              className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
            >
              {queued_id ? 'Close' : t('actions.cancel')}
            </button>
            {/* Submit hides only in the offline-queued state — the row is
                already durable; resubmitting would create a duplicate. Online
                success unmounts the modal entirely, so this branch is the
                queued case alone. */}
            {!queued_id && (
              <button
                type="submit"
                disabled={m.isPending || amount_rupees <= 0}
                className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
              >
                {m.isPending ? '…' : t('mark_paid.submit')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
